"use client";

import { useRef } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  X,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useChecklistState,
  CELEBRATION_TEXT,
} from "@/components/onboarding/use-checklist-state";
import { ToolNavigationLink } from "@/components/shell/ToolNavigationLink";

interface OnboardingChecklistProps {
  hasOnboardingPreferences: boolean;
  collapsed?: boolean;
  dismissed?: boolean;
  onNavigate?: () => void;
}

export function OnboardingChecklist({
  hasOnboardingPreferences,
  collapsed = false,
  dismissed = false,
  onNavigate,
}: OnboardingChecklistProps) {
  const {
    planType,
    items,
    completedCount,
    totalCount,
    progressPercent,
    allComplete,
    isItemComplete,
    shouldShow,
    isCollapsed,
    isAnimatingOut,
    handleToggle,
    handleDismiss,
    handleItemClick,
    handleTransitionEnd,
  } = useChecklistState({
    hasOnboardingPreferences,
    checklistDismissed: dismissed,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  if (!shouldShow || collapsed) return null;

  const celebrationText =
    planType && CELEBRATION_TEXT[planType]
      ? CELEBRATION_TEXT[planType]
      : "You're ready to compare the board.";

  return (
    <div
      ref={containerRef}
      className={cn(
        "mx-2 mb-2 transition-all duration-500 ease-in-out overflow-hidden",
        isAnimatingOut && "opacity-0 max-h-0 !mb-0 !mt-0",
        !isAnimatingOut && "opacity-100 max-h-[500px]",
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="rounded-panel border border-white/[0.08] bg-white/[0.02] overflow-hidden">
        <button
          type="button"
          onClick={handleToggle}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
        >
          <Sparkles className="size-3.5 text-primary shrink-0" />
          <span className="text-xs font-semibold text-foreground/80 flex-1">
            {allComplete ? "All set!" : "Getting Started"}
          </span>
          <span className="text-[10px] text-muted-foreground/60 tabular-nums">
            {completedCount}/{totalCount}
          </span>
          {isCollapsed ? (
            <ChevronDown className="size-3 text-muted-foreground/40" />
          ) : (
            <ChevronUp className="size-3 text-muted-foreground/40" />
          )}
        </button>

        <div className="px-3 pb-1">
          <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {!isCollapsed && (
          <div className="px-2 pb-2 pt-1 space-y-0.5">
            {items.map((item) => {
              const isDone = isItemComplete(item.id);
              const content = (
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors",
                    item.href && !isDone
                      ? "hover:bg-white/[0.04] cursor-pointer"
                      : "",
                  )}
                >
                  <div
                    className={cn(
                      "h-4 w-4 rounded-full shrink-0 flex items-center justify-center border transition-colors",
                      isDone
                        ? "bg-success/80 border-success/60"
                        : "border-white/20 bg-transparent",
                    )}
                  >
                    {isDone && <Check className="size-2.5 text-background" />}
                  </div>

                  <span
                    className={cn(
                      "text-xs flex-1",
                      isDone
                        ? "text-muted-foreground/50 line-through"
                        : "text-foreground/70",
                    )}
                  >
                    {item.label}
                  </span>

                  {item.href && !isDone && (
                    <ArrowRight className="size-3 text-muted-foreground/30" />
                  )}
                </div>
              );

              if (item.href && !isDone) {
                return (
                  <ToolNavigationLink
                    key={item.id}
                    href={item.href}
                    onClick={() => {
                      handleItemClick(item);
                      onNavigate?.();
                    }}
                  >
                    {content}
                  </ToolNavigationLink>
                );
              }

              return <div key={item.id}>{content}</div>;
            })}

            {!allComplete && (
              <button
                type="button"
                onClick={handleDismiss}
                className="flex items-center gap-1.5 px-2 pt-1.5 text-[10px] text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors"
              >
                <X className="size-2.5" />
                Dismiss
              </button>
            )}

            {allComplete && (
              <div className="text-center pt-1.5">
                <p className="text-[11px] text-accent-value">
                  {celebrationText}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
